import '../../../../assets/stylesheets/base/tokens.css';
import '../../../../assets/stylesheets/design-system/components/button/_button.scss';
import '../../../../assets/stylesheets/design-system/components/layout/_layout.scss';
import '../../../../assets/stylesheets/design-system/components/modal/_modal.scss';
import '../../../../assets/stylesheets/design-system/components/modal/_modal-header.scss';
import '../../../../assets/stylesheets/design-system/components/typestyle/_typestyle.scss';
import './styles.scss';

import {
  buildSuppliedListingLinkWarningContent,
  WARNING_DESCRIPTION_ID,
  WARNING_TITLE_ID,
} from './content';

const LINK_SELECTOR = '.js-supplied-listing-link';
const MODAL_ID = 'supplied-listing-link-warning-modal';
const WEB_PROTOCOLS = ['http:', 'https:'];
const ALLOWED_PROTOCOLS = [...WEB_PROTOCOLS, 'mailto:'];
const MAIL_ADDRESS_PATTERN = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
const WEBSITE_SEO_INTEGRATION_SELECTOR = '.js-website-seo-integration-enabled';

function parseDestination(link) {
  try {
    const url = new URL(link.href, window.location.href);
    if (!ALLOWED_PROTOCOLS.includes(url.protocol)) return null;

    if (WEB_PROTOCOLS.includes(url.protocol)) {
      if (!url.hostname || url.username || url.password) return null;

      return {href: url.href, label: url.host, opensNewContext: true};
    }

    let label = url.pathname;
    try {
      label = decodeURIComponent(label);
    } catch {
      return null;
    }
    if (!MAIL_ADDRESS_PATTERN.test(label)) return null;

    return {href: url.href, label, opensNewContext: false};
  } catch {
    return null;
  }
}

function positionDialogNearSource(dialog, sourceLink) {
  if (
    !dialog ||
    !sourceLink ||
    window.self === window.top ||
    !document.querySelector(WEBSITE_SEO_INTEGRATION_SELECTOR)
  ) return;

  const overlay = dialog.closest('.js-modal-overlay');
  if (!overlay) return;

  // WSI frames expand to the full document, so their center can be outside the customer's viewport.
  const overlayRect = overlay.getBoundingClientRect();
  const overlayStyle = window.getComputedStyle(overlay);
  const availableTop = overlayRect.top + (parseFloat(overlayStyle.paddingTop) || 0);
  const availableBottom = overlayRect.bottom - (parseFloat(overlayStyle.paddingBottom) || 0);
  const sourceRect = sourceLink.getBoundingClientRect();
  const dialogHeight = dialog.getBoundingClientRect().height;
  const desiredTop = sourceRect.top + sourceRect.height / 2 - dialogHeight / 2;
  const maximumTop = Math.max(availableBottom - dialogHeight, availableTop);
  const dialogTop = Math.min(Math.max(desiredTop, availableTop), maximumTop);

  dialog.style.alignSelf = 'flex-start';
  dialog.style.marginBlock = '0';
  dialog.style.marginBlockStart = `${dialogTop - availableTop}px`;
}

function configureOpenedModal(sourceLink) {
  const modal = document.getElementById(MODAL_ID);
  if (!modal) return;

  modal.setAttribute('aria-labelledby', WARNING_TITLE_ID);
  modal.setAttribute('aria-describedby', WARNING_DESCRIPTION_ID);

  const dialog = modal.querySelector('[role="dialog"]');
  dialog?.classList.add('supplied-listing-link-warning-dialog');
  dialog?.setAttribute('aria-labelledby', WARNING_TITLE_ID);
  dialog?.setAttribute('aria-describedby', WARNING_DESCRIPTION_ID);

  modal.querySelector('.js-modal-content')
    ?.classList.add('supplied-listing-link-warning-dialog__content');

  positionDialogNearSource(dialog, sourceLink);

  const continueLink = modal.querySelector('.js-supplied-listing-link-continue');
  continueLink?.addEventListener('click', () => {
    window.setTimeout(() => {
      document.dispatchEvent(new CustomEvent('sm-modal:close', {detail: {modalId: MODAL_ID}}));
    });
  });

  modal.querySelector('.js-supplied-listing-link-cancel')?.focus({preventScroll: true});
}

function openWarning(destination, sourceLink) {
  const content = buildSuppliedListingLinkWarningContent({
    destination: destination.label,
    href: destination.href,
    opensNewContext: destination.opensNewContext,
  });

  document.dispatchEvent(new CustomEvent('sm-modal:open', {
    detail: {
      modalId: MODAL_ID,
      content,
      contentOwnsCloseButton: true,
      onComplete: () => configureOpenedModal(sourceLink),
    },
  }));
}

function handleSuppliedLink(event) {
  if (event.type === 'auxclick' && event.button !== 1) return;

  const link = event.target.closest?.(LINK_SELECTOR);
  if (!link) return;

  event.preventDefault();
  event.stopPropagation();

  const destination = parseDestination(link);
  if (destination) openWarning(destination, link);
}

document.addEventListener('click', handleSuppliedLink);
document.addEventListener('auxclick', handleSuppliedLink);
